import { Box, Grid, styled, Tooltip, Typography } from '@mui/material';
import DocumentIntroTitle from 'FeatureModules/Document/Components/DocumentIntroTitle';
import Loader from 'Components/Forms/Loader';
import useDialog from 'hooks/useDialog';
import { usePartnerRequest } from 'FeatureModules/Partner/Components/hooks';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import FlexBox from 'Components/FlexBox';
import HtmlField from 'Components/HTMLField';
import QuestionnaireItem from 'FeatureModules/Questionnaire/Components/QuestionnaireResponseTable/QuestionnaireItem';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { CardContainer, ItemCardSmall, ThemeButton, ThemeTypography } from 'DesignSystem';
import ItemProgressTitle from 'DesignSystem/DataDisplay/ItemProgressTitle/ItemProgressTitle';
import Logs from 'DesignSystem/DataDisplay/Logs/Logs';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import truncate from 'lodash/truncate';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tbooleanish, Booleanish } from 'types/booleanish.types';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import {
  EPartnerRequestStatus,
  TPartnerRequestStatus,
  ERequestType,
} from 'FeatureModules/Partner/types/partner.types';
import { TRouteParamsWithId } from 'types/router.types';
import { convertHexToRGBarray } from 'utils/color.utils';
import CompanyNameLogo from 'Components/CompanyNameLogo';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';
import PageSubTitle from 'Components/PageSubTitle';
import PageTitle from 'Components/PageTitle';
import {
  REQUEST_LOG_ACTION_TITLES,
  REQUEST_STATUS_CLIENT,
  REQUEST_STATUS_COLOR_MAPPING,
} from 'FeatureModules/Request/utils/requestStatus.utils';

const Status = styled(Typography)<{ status: TPartnerRequestStatus }>(({ status }) => ({
  fontSize: 13,
  marginRight: 8,
  textTransform: 'uppercase',
  color: REQUEST_STATUS_COLOR_MAPPING[status],
}));

const RequestItem = styled(Box)<{
  transparent?: Tbooleanish;
}>(({ theme, transparent }) => ({
  width: '100%',
  backgroundColor: '#fff',
  padding: theme.spacing(1, 2),
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,

  ...(transparent === 'true'
    ? {
        boxShadow: 'none',
        border: `1px dashed rgba(${convertHexToRGBarray(
          theme.custom.colors.blueFrost
        )}, 0.4) !important`,
      }
    : {}),
}));

const MessageBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  backgroundColor: theme.custom.themeColors.grayScale[20],
  padding: theme.spacing(2),
  borderBottomLeftRadius: 4,
  borderBottomRightRadius: 4,
}));

const MessageTitle = styled(ThemeTypography)(({ theme }) => ({
  whiteSpace: 'nowrap',
}));

const LogContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: 6,
  padding: theme.spacing(2),
  border: `1px solid ${theme.custom.themeColors.grayScale[10]}`,
}));

const ItemTitle = styled(ThemeTypography)(() => ({
  textTransform: 'uppercase',
  letterSpacing: 1.5,
}));

const DismissedContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(2.5),
  borderRadius: 6,
}));

const DismissedMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: theme.custom.themeColors.grayScale[20],
  padding: theme.spacing(1),
  borderRadius: 6,
}));

const RequestItemTitle = styled(Box)<{
  transparent?: Tbooleanish;
}>(({ transparent }) => ({
  opacity: transparent === 'true' ? 0.4 : 1,
}));

const PartnerRequestOverview = () => {
  const { error, loading, partnerRequest, questionnaire } = usePartnerRequest();
  const { handleDeletePartnerRequest } = usePartnerRequestActions();
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const { id } = useParams<TRouteParamsWithId>();
  const { plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;

  const isRequested =
    partnerRequest?.requestStatus &&
    EPartnerRequestStatus[partnerRequest.requestStatus] === EPartnerRequestStatus.REQUESTED;

  const isDeleted =
    partnerRequest?.requestStatus &&
    EPartnerRequestStatus[partnerRequest?.requestStatus] === EPartnerRequestStatus.DELETED;

  const isDenied =
    partnerRequest?.requestStatus &&
    EPartnerRequestStatus[partnerRequest?.requestStatus] === EPartnerRequestStatus.DENIED;

  const isAccepted =
    partnerRequest?.requestStatus &&
    EPartnerRequestStatus[partnerRequest?.requestStatus] ===
      EPartnerRequestStatus.PROVIDED_COMPLETED;

  const isProvided =
    partnerRequest?.requestStatus &&
    EPartnerRequestStatus[partnerRequest?.requestStatus] === EPartnerRequestStatus.PROVIDED;

  const isEdited =
    partnerRequest?.requestStatus &&
    EPartnerRequestStatus[partnerRequest?.requestStatus] === EPartnerRequestStatus.EDITED;

  const displayChain =
    partnerRequest?.requestType === ERequestType.CHAIN && (isProvided || isAccepted);

  const logs = useMemo(() => {
    if (!partnerRequest) return [];
    return [...partnerRequest.logs].reverse().map(log => ({
      title: REQUEST_LOG_ACTION_TITLES[log.action],
      date: log.timestamp,
      logo: log.company.logo?.url,
      author: `${log.user.firstName} ${log.user.lastName}`,
      completed: log.action === 'COMPLETED',
    }));
  }, [partnerRequest]);

  const handleReviewAndApprove = () => {
    if (!partnerRequest) return;

    openDialog({
      type: 'REVIEW_DOCUMENT_REQUEST',
      props: {
        request: partnerRequest,
      },
    });
  };

  const handleEditRequest = () => {
    if (!partnerRequest) return;

    openDialog({
      type: 'PARTNER_REQUEST',
      props: {
        requestedToCompany: partnerRequest.requestedToCompany,
        initialRequest: partnerRequest,
      },
    });
  };

  const handleDeleteRequest = () => {
    if (!partnerRequest) return;

    handleDeletePartnerRequest(partnerRequest.id);
  };

  const handleClickFulfillRequestButton = () => {
    if (!partnerRequest) return;

    openDialog({
      type: 'REVIEW_CHAIN_REQUEST',
      props: { request: partnerRequest },
    });
  };

  const handleNavigateToChain = () => {
    navigate(`/component-chains/${partnerRequest?.chainId}/editor`);
  };

  const handleNavigateToQuestionnaire = () => {
    navigate(`/questionnaires/${partnerRequest?.questionnaireId}}`);
  };

  if (error) return <ErrorState />;

  if (loading || !partnerRequest) return <Loader />;

  return (
    <PageContainer>
      <PageTitle
        title="Partner requests"
        goBackLabel="All Requests"
        goBackUrl={`/partners/${id}/requests`}
      />
      <Box mt={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={8}>
            <RequestItem
              display="flex"
              justifyContent="space-between"
              transparent={Booleanish(isRequested || isEdited)}
              data-testid="request-item"
            >
              <RequestItemTitle
                display="flex"
                alignContent="center"
                transparent={Booleanish(isRequested || isEdited)}
              >
                <FlexBox mr={1.5}>
                  <Icon
                    name={
                      REQUEST_STATUS_CLIENT[partnerRequest.requestType][
                        partnerRequest.requestStatus
                      ].iconName
                    }
                    size="xx-large"
                  />
                </FlexBox>
                <Box>
                  <Box display="flex" alignItems="center">
                    <Tooltip title={partnerRequest.requestTitle || ''}>
                      <ThemeTypography variant="ITEM_TITLE">
                        {truncate(partnerRequest.requestTitle, {
                          length: 45,
                          separator: '',
                        })}
                      </ThemeTypography>
                    </Tooltip>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Status variant="body2" status={partnerRequest.requestStatus}>
                      {
                        REQUEST_STATUS_CLIENT[partnerRequest.requestType][
                          partnerRequest.requestStatus
                        ].statusTitle
                      }
                    </Status>
                  </Box>
                </Box>
              </RequestItemTitle>
              <Box display="flex">
                {!isDenied && !isDeleted && !isAccepted && (
                  <ThemeButton
                    size="small"
                    color="BLUE_ICE"
                    onClick={handleEditRequest}
                    startIcon={<Icon name="edit" size="small" color="gray-60" />}
                  >
                    Edit
                  </ThemeButton>
                )}
                {isRequested && (
                  <Box ml={1} display="flex" alignItems="center">
                    <ThemeButton
                      size="small"
                      color="BLUE_ICE"
                      onClick={handleDeleteRequest}
                      startIcon={<Icon name="delete" size="small" color="gray-60" />}
                    >
                      Delete
                    </ThemeButton>
                  </Box>
                )}
              </Box>
            </RequestItem>
            <MessageBox>
              <MessageTitle variant="BODY_LARGE_BOLD">Your message:&nbsp;</MessageTitle>
              <HtmlField>{partnerRequest.requestMessage}</HtmlField>
            </MessageBox>

            {partnerRequest.requestStatus === 'DENIED' && (
              <DismissedContainer mt={3} data-testid="dismissed-container">
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center">
                    <CompanyNameLogo
                      size={EAvailableSizes.MEDIUM}
                      companyName={partnerRequest.requestedToCompany.name}
                    />
                    <ThemeTypography variant="BODY_LARGE_BOLD">
                      &nbsp;has dismissed your request
                    </ThemeTypography>
                  </Box>
                  <ThemeButton
                    variant="contained"
                    size="medium"
                    startIcon={<Icon name="edit" size="medium" />}
                    onClick={handleEditRequest}
                  >
                    Respond & edit request
                  </ThemeButton>
                </Box>
                <DismissedMessage mt={2}>
                  <MessageTitle variant="BODY_LARGE_BOLD">
                    {partnerRequest.requestedToCompany.name}:&nbsp;
                  </MessageTitle>
                  <HtmlField>{partnerRequest?.requestReplyMessage || ''}</HtmlField>
                </DismissedMessage>
              </DismissedContainer>
            )}

            {partnerRequest.questionnaireId && questionnaire && (
              <Box mt={2}>
                <QuestionnaireItem
                  questionnaire={questionnaire}
                  onClick={handleNavigateToQuestionnaire}
                />
              </Box>
            )}

            {partnerRequest.document && (
              <Box mt={4}>
                <Box display="flex" alignItems="center" mb={1.5}>
                  {partnerRequest.requestStatus === 'PROVIDED' && (
                    <>
                      <CompanyNameLogo
                        size={EAvailableSizes.MEDIUM}
                        companyName={partnerRequest.requestedToCompany.name}
                      />
                      <ThemeTypography variant="BODY_LARGE_BOLD">
                        &nbsp;has uploaded a new document
                      </ThemeTypography>
                    </>
                  )}

                  {partnerRequest.requestStatus === 'PROVIDED_COMPLETED' && (
                    <ItemTitle variant="TITLE_EXTRA_SMALL">Accepted document</ItemTitle>
                  )}
                </Box>

                <ItemCardSmall>
                  <Box display="flex" width="100%" justifyContent="space-between">
                    <DocumentIntroTitle document={partnerRequest.document} showOpenInNewTab />
                    {!isPartner && (
                      <>
                        {partnerRequest.requestStatus === 'PROVIDED_COMPLETED' ? (
                          <ThemeButton
                            variant="contained"
                            size="medium"
                            startIcon={<Icon name="plus" size="small" color="WHITE" />}
                            onClick={() => openDialog({ type: 'CREATE_IMPACT_CLAIM' })}
                            data-testid="add-impact-claim-btn"
                          >
                            Add impact claim
                          </ThemeButton>
                        ) : (
                          <ThemeButton
                            variant="contained"
                            size="medium"
                            startIcon={
                              <Icon name="document-request-pending" size="large" color="WHITE" />
                            }
                            onClick={handleReviewAndApprove}
                            data-testid="review-approve-btn"
                          >
                            Review & approve
                          </ThemeButton>
                        )}
                      </>
                    )}
                  </Box>
                </ItemCardSmall>
                {!isPartner && (
                  <Box mt={2} display="flex" alignItems="center">
                    <Box mr={1}>
                      <Icon name="info" size="medium" />
                    </Box>
                    <ThemeTypography variant="BODY_LARGE">
                      Uploaded documents can also be managed in your document library under “Shared
                      with me”
                    </ThemeTypography>
                  </Box>
                )}
              </Box>
            )}
            {!!partnerRequest.proofDocuments?.length && (
              <Box mt={2}>
                <PageSubTitle title="Uploaded proof document" />
                {partnerRequest.proofDocuments.map(proofDocument => (
                  <ItemCardSmall key={proofDocument.id} disabled>
                    <DocumentIntroTitle document={proofDocument} showOpenInNewTab />
                  </ItemCardSmall>
                ))}
              </Box>
            )}

            {!!partnerRequest.previouslyUploadedDocuments?.length && (
              <Box mt={2}>
                <PageSubTitle title="Previously uploaded documents" />
                {partnerRequest.previouslyUploadedDocuments.map(previousDocument => (
                  <ItemCardSmall key={previousDocument.id} disabled>
                    <DocumentIntroTitle document={previousDocument} showOpenInNewTab />
                  </ItemCardSmall>
                ))}
              </Box>
            )}
            {displayChain && (
              <Box mt={4}>
                <Box width="100%" display="flex" alignItems="center">
                  <CompanyNameLogo
                    size={EAvailableSizes.MEDIUM}
                    companyName={partnerRequest.requestedToCompany.name}
                  />
                  <ThemeTypography variant="BODY_LARGE_BOLD">
                    &nbsp;has finished this chain mapping
                  </ThemeTypography>
                </Box>
                <CardContainer padding={1} mt={2}>
                  <Box display="flex" justifyContent="space-between" position="relative">
                    <Box display="flex" width="100%" justifyContent="space-between">
                      <ItemProgressTitle
                        title={partnerRequest?.chain?.title || ''}
                        imageUrl={partnerRequest?.chain?.image?.url}
                        taskCompleted={partnerRequest?.chain?.chainTasksDetails.completed || 0}
                        taskTotal={partnerRequest?.chain?.chainTasksDetails.total || 0}
                        backupIcon={
                          <Icon name="component-chain-image" size="xx-large" color="blue-ice" />
                        }
                      />
                      {EPartnerRequestStatus[partnerRequest.requestStatus] ===
                      EPartnerRequestStatus.PROVIDED_COMPLETED ? (
                        <ThemeButton
                          variant="contained"
                          startIcon={<Icon name="chain-request-manage" color="WHITE" />}
                          onClick={handleNavigateToChain}
                        >
                          Go to chain
                        </ThemeButton>
                      ) : (
                        <ThemeButton
                          variant="contained"
                          startIcon={<Icon name="chain-request-manage" color="WHITE" />}
                          onClick={handleClickFulfillRequestButton}
                        >
                          Review and approve
                        </ThemeButton>
                      )}
                    </Box>
                  </Box>
                </CardContainer>
                {!isPartner && (
                  <Box mt={2} display="flex" alignItems="center">
                    <Box mr={1}>
                      <Icon name="info" size="medium" />
                    </Box>
                    <ThemeTypography variant="BODY_LARGE">
                      Mapped partner chains can also be managed in your <b>Components</b>, or in
                      <b> Partners </b>
                      under “Partner components”.
                    </ThemeTypography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
          {logs.length > 0 && (
            <Grid item xs={12} sm={4}>
              <LogContainer>
                <Logs logs={logs} data-testid="partner-logs" />
              </LogContainer>
            </Grid>
          )}
          <Grid item xs={12} sm={4}></Grid>
        </Grid>
      </Box>
    </PageContainer>
  );
};

export default PartnerRequestOverview;
